import type {
  CartItemAccommodationFragment,
  CartItemBeachChairFragment,
  CartItemExperienceFragment,
  CartItemRentableFragment,
  CartQuery,
} from '@data-access/lib/gql/cart/documents'
import { CartDocument } from '@data-access/lib/gql/cart/documents'
import { useGraphQL } from '@data-access/lib/graphql-query'
import type { VariablesOf } from '@graphql-typed-document-node/core'
import { sortBy } from 'es-toolkit'

export type CartItemExperience = CartItemExperienceFragment & {
  __typename: 'CartItemExperience'
  promotionCode: PromotionCode
}

export type CartItemBeachChair = CartItemBeachChairFragment & {
  __typename: 'CartItemBeachChair'
  promotionCode: PromotionCode
}

export type CartItemAccommodation = CartItemAccommodationFragment & {
  __typename: 'CartItemAccommodation'
  promotionCode: PromotionCode
}

export type CartItemRentable = CartItemRentableFragment & {
  __typename: 'CartItemRentable'
  promotionCode: PromotionCode
}

type PromotionCode = {
  name?: string | null
  discount?: number | null
  value?: number | null
  type?: string | null
  totalAfterDiscount?: number | null
}

export type Cart = CartData
export type CartItem =
  | CartItemExperience
  | CartItemBeachChair
  | CartItemAccommodation
  | CartItemRentable
export type CartItems = CartData['items']

type UseCartOptions = Parameters<typeof useGraphQL<CartQuery, VariablesOf<typeof CartDocument>>>[2]

export const useCart = (options: UseCartOptions = {}) => {
  const { data, isLoading, isRefetching, isFetching, dataUpdatedAt, refetch } = useGraphQL(
    CartDocument,
    undefined,
    { refetchOnWindowFocus: true, ...options, select }
  )

  return { cart: data, isLoading, isFetching, isRefetching, dataUpdatedAt, refetch }
}

type CartData = Omit<CartQuery['cart'], 'items'> & {
  isEmpty: boolean
  items: {
    all: CartItem[]
    experiences: CartItemExperience[]
    accommodations: CartItemAccommodation[]
    rentables: CartItemRentable[]
  }
}

const select = ({ cart }: CartQuery): CartData => {
  const all = cart.items.map(item => {
    const promotionCode = {
      name: item.promotionCodeName,
      discount: item.promotionCodeDiscount,
      value: item.promotionCodeValue,
      type: item.promotionCodeType,
      totalAfterDiscount: item.totalAfterDiscount,
    }

    if (item.__typename === 'CartItemExperience') {
      return { ...item, promotionCode, tickets: getTickets(item) }
    }

    return { ...item, promotionCode }
  })

  const experiences = getExperienceItems(all)
  const accommodations = getAccommodationItems(all)
  const rentables = getRentableItems(all)

  return {
    ...cart,
    isEmpty: all.length === 0,
    items: { all, experiences, accommodations, rentables },
  }
}

const getAccommodationItems = (items: CartItem[]) =>
  items.filter((item): item is CartItemAccommodation => item.__typename === 'CartItemAccommodation')

const getExperienceItems = (items: CartItem[]) =>
  items.filter((item): item is CartItemExperience => item.__typename === 'CartItemExperience')

const getRentableItems = (items: CartItem[]) =>
  items.filter((item): item is CartItemRentable => item.__typename === 'CartItemRentable')

const getTickets = (cartItem: CartItemExperienceFragment) => {
  const tickets = cartItem.tickets

  return sortBy(tickets, ['price']).toReversed()
}
